import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { memo, useMemo } from 'react';
import { useTranslation } from '../../../../hooks/useTranslation';
import { pushQueries } from '../../../../utils';
import { PAGE_KEY } from '../../../../utils/const';
import { PaginationConfig } from '../../utils/types';
import { Pagination } from 'components/DataTable';

interface DataPaginationProps extends PaginationConfig {
  onPageChange: (page: number) => void;
  pagination?: Pagination;
}

const DataPagination = memo(
  ({
    currentPage,
    totalItems,
    itemsPerPage,
    pagination,
    onPageChange,
  }: DataPaginationProps) => {
    const { t } = useTranslation();

    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    const goToPage = (page: number) => {
      if (page >= 1 && page <= totalPages) {
        onPageChange(page);
        pushQueries({ [pagination?.pageKey || PAGE_KEY]: page.toString() });
      }
    };

    const pagesToDisplay = useMemo(() => {
      const visiblePages = 5;
      const pages: Array<number | string> = [];

      if (totalPages <= visiblePages) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }

      pages.push(1);
      const left = Math.max(2, currentPage - 1);
      const right = Math.min(totalPages - 1, currentPage + 1);

      if (left > 2) pages.push('...');
      for (let i = left; i <= right; i++) pages.push(i);
      if (right < totalPages - 1) pages.push('...');
      pages.push(totalPages);

      return pages;
    }, [currentPage, totalPages]);

    const renderPageButton = (page: number | string, index: number) => {
      if (typeof page === 'string') {
        return (
          <li key={`ellipsis-${index}`} className="page-item disabled">
            <span className="page-link">{page}</span>
          </li>
        );
      }

      return (
        <li
          key={page}
          className={`page-item ${page === currentPage ? 'active' : ''}`}
        >
          <button className="page-link" onClick={() => goToPage(page)}>
            {page}
          </button>
        </li>
      );
    };

    return (
      <div className="card-footer d-md-flex align-items-center">
        <p className="m-0 text-secondary mb-2 mb-md-0">
          {t('SHOWING')} <span>{startItem}</span> {t('TO')}{' '}
          <span>{endItem}</span> {t('OF')} <span>{totalItems}</span>{' '}
          {t('ENTRIES')}
        </p>
        <ul className="pagination m-0 ms-auto">
          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <IconChevronLeft size={14} />
              {t('PREV')}
            </button>
          </li>

          {pagesToDisplay.map(renderPageButton)}

          <li
            className={`page-item ${
              currentPage === totalPages ? 'disabled' : ''
            }`}
          >
            <button
              className="page-link"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              {t('NEXT')}
              <IconChevronRight size={14} />
            </button>
          </li>
        </ul>
      </div>
    );
  },
);

DataPagination.displayName = 'DataPagination';

export default DataPagination;
